import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import toast from "react-hot-toast";
import {
  businessAutoLogin,
  businessAcceptRejectRequest,
} from "../../API/ApportunityRequest/ApportunityRequestApi";
import ImagePopUpModal from "../../components/Modal/ImagePopUpModal";
import {
  FiHome,
  FiMapPin,
  FiPhone,
  FiGlobe,
  FiStar,
  FiUser,
  FiMessageSquare,
  FiExternalLink,
  FiCheck,
  FiX,
  FiSend,
  FiClock,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

const BusinessOpportunity = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [opportunityRequest, setOpportunityRequest] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [action, setAction] = useState(null); // 'accept' or 'reject'
  const [message, setMessage] = useState("");
  const [showResponseForm, setShowResponseForm] = useState(false);
  const responseFormRef = useRef(null);

  // Scroll to response form when it opens
  useEffect(() => {
    if (showResponseForm && responseFormRef.current) {
      setTimeout(() => {
        responseFormRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [showResponseForm]);

  const fetchOpportunityRequest = useCallback(async () => {
    try {
      setLoading(true);
      const data = await businessAutoLogin(token);
      setOpportunityRequest(data.opportunityRequest);
      setConversations(data.conversations || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error loading opportunity request"
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid access token");
      return;
    }
    fetchOpportunityRequest();
  }, [token, fetchOpportunityRequest]);

  const handleAcceptReject = async () => {
    if (!action) {
      toast.error("Please select an action");
      return;
    }

    try {
      setSubmitting(true);
      const data = await businessAcceptRejectRequest(token, {
        opportunityRequestId: opportunityRequest._id,
        action,
        message: message.trim() || undefined,
      });

      setOpportunityRequest(data.opportunityRequest);
      setConversations(data.conversations || []);
      setShowResponseForm(false);
      setAction(null);
      setMessage("");

      toast.success(
        `Request ${action === "accept" ? "accepted" : "rejected"} successfully`
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error processing request"
      );
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: FiClock, text: "Pending" },
      in_progress: { color: "bg-blue-100 text-blue-800", icon: FiClock, text: "In Progress" },
      completed: { color: "bg-green-100 text-green-800", icon: FiCheckCircle, text: "Completed" },
      rejected: { color: "bg-red-100 text-red-800", icon: FiXCircle, text: "Rejected" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}
      >
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  const getActionLabel = (action) => {
    const labels = {
      submitted: "Submitted",
      accepted: "Accepted",
      rejected: "Rejected",
      message: "Message",
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader />
      </div>
    );
  }

  if (!opportunityRequest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Opportunity Request Not Found
          </h2>
          <p className="text-gray-600">The request you're looking for doesn't exist or has expired.</p>
        </div>
      </div>
    );
  }

  // Show buttons if:
  // 1. Status is pending (initial request) - show Accept/Reject
  // 2. Status is in_progress AND last conversation is work_submitted - show Complete Contract/Reject Work
  const lastConversation = conversations.length > 0 ? conversations[conversations.length - 1] : null;
  const isWorkSubmitted = lastConversation?.action === "work_submitted";

  const canRespond = opportunityRequest.contractStatus === "pending";
  const canCompleteContract =
    opportunityRequest.contractStatus === "in_progress" &&
    isWorkSubmitted &&
    !showResponseForm;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Opportunity Request
              </h1>
              <div className="flex items-center gap-3">
                {getStatusBadge(opportunityRequest.contractStatus)}
                <span className="text-sm text-gray-500">
                  Requested on{" "}
                  {new Date(opportunityRequest.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            {canRespond && !showResponseForm && (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAction("accept");
                    setShowResponseForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <FiCheck className="w-5 h-5" />
                  Accept
                </button>
                <button
                  onClick={() => {
                    setAction("reject");
                    setShowResponseForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <FiX className="w-5 h-5" />
                  Reject
                </button>
              </div>
            )}
            {canCompleteContract && (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAction("accept");
                    setShowResponseForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <FiCheckCircle className="w-5 h-5" />
                  Complete Contract
                </button>
                <button
                  onClick={() => {
                    setAction("reject");
                    setShowResponseForm(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <FiX className="w-5 h-5" />
                  Reject Work
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Request Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Athlete Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiUser className="w-5 h-5" />
                Athlete Information
              </h2>
              <div className="flex items-start gap-4">
                {opportunityRequest.userId?.profilePhoto ? (
                  <img
                    src={opportunityRequest.userId.profilePhoto}
                    alt="Athlete"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                    <FiUser className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {opportunityRequest.userId?.firstName}{" "}
                    {opportunityRequest.userId?.lastName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {opportunityRequest.userId?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiHome className="w-5 h-5" />
                Business Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {opportunityRequest.businessInfo.business_name}
                  </p>
                </div>

                {opportunityRequest.businessInfo.address && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <FiMapPin className="w-4 h-4" />
                      Address
                    </label>
                    <p className="text-sm text-gray-900">
                      {opportunityRequest.businessInfo.address}
                    </p>
                  </div>
                )}

                {opportunityRequest.businessInfo.contact_no && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <FiPhone className="w-4 h-4" />
                      Contact Number
                    </label>
                    <p className="text-sm text-gray-900">
                      {opportunityRequest.businessInfo.contact_no}
                    </p>
                  </div>
                )}

                {opportunityRequest.businessInfo.website_link && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <FiGlobe className="w-4 h-4" />
                      Website
                    </label>
                    <a
                      href={opportunityRequest.businessInfo.website_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                    >
                      {opportunityRequest.businessInfo.website_link}
                      <FiExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {opportunityRequest.businessInfo.rating && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <FiStar className="w-4 h-4" />
                      Rating
                    </label>
                    <div className="flex items-center gap-1">
                      <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-medium text-gray-900">
                        {opportunityRequest.businessInfo.rating}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Business Photos */}
              {opportunityRequest.businessInfo.photos?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Business Photos
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {opportunityRequest.businessInfo.photos.map((photo, idx) => (
                      <ImagePopUpModal
                        key={idx}
                        imageUrl={photo}
                        customClass="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                        alt="Business photo"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Response Form */}
            {showResponseForm && (
              <div
                ref={responseFormRef}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {isWorkSubmitted && action === "accept"
                    ? "Complete Contract"
                    : isWorkSubmitted && action === "reject"
                      ? "Reject Work"
                      : action === "accept"
                        ? "Accept Request"
                        : "Reject Request"}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={`Add a message for the athlete...`}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows="4"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAcceptReject}
                      disabled={submitting}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${action === "accept"
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-red-600 text-white hover:bg-red-700"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {submitting ? (
                        <>
                          <Loader />
                          Processing...
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4" />
                          {action === "accept" ? "Accept" : "Reject"} Request
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowResponseForm(false);
                        setAction(null);
                        setMessage("");
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Conversation Timeline */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiMessageSquare className="w-5 h-5" />
                Conversation
              </h2>
              <div className="space-y-4">
                {conversations.length > 0 ? (
                  <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                    {conversations.map((conversation, index) => (
                      <div
                        key={conversation._id}
                        className="relative flex items-start gap-4 pb-6 last:pb-0"
                      >
                        {/* Timeline Dot */}
                        <div
                          className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${conversation.senderType === "athlete"
                            ? "bg-blue-100"
                            : conversation.action === "accepted"
                              ? "bg-green-100"
                              : conversation.action === "rejected"
                                ? "bg-red-100"
                                : "bg-gray-100"
                            }`}
                        >
                          {conversation.senderType === "athlete" ? (
                            <FiUser
                              className={`w-4 h-4 ${conversation.senderType === "athlete"
                                ? "text-blue-600"
                                : "text-gray-600"
                                }`}
                            />
                          ) : conversation.action === "accepted" ? (
                            <FiCheck className="w-4 h-4 text-green-600" />
                          ) : conversation.action === "rejected" ? (
                            <FiX className="w-4 h-4 text-red-600" />
                          ) : (
                            <FiMessageSquare className="w-4 h-4 text-gray-600" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {conversation.senderType === "athlete"
                                ? "Athlete"
                                : "Business"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {getActionLabel(conversation.action)}
                            </span>
                            {/* Show status badge only for accept/reject actions */}
                            {conversation.action === "accepted" && (
                              <span className="text-xs">
                                {getStatusBadge(conversation.contractStatus || "in_progress")}
                              </span>
                            )}
                            {conversation.action === "rejected" && (
                              <span className="text-xs">
                                {getStatusBadge("rejected")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-2">
                            {conversation.message}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(conversation.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No conversation yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessOpportunity;

